import { HttpApiEndpoint, HttpApiGroup } from "@effect/platform";
import * as S from "effect/Schema";
import { ContractType } from "./admin-clients.js";
import { PagingParams, pagedResponse } from "./paging.js";
import { PrincipalMiddleware } from "./principal.js";
import { Forbidden } from "./shares.js";
import { LanguageSchema } from "./column-schemas.js";
/**
 * An `online_courses` row — the admin course-catalog screen's list/editor.
 * Courses carry no `client_id` (global content, gated to clients through
 * `online_course_shares` / `client_shares`, not ownership); `contract_types`
 * narrows which tenant types the course is even offered to, independent of
 * per-client sharing.
 */
export const OnlineCourseRow = S.Struct({
    id: S.UUID,
    title: S.NullOr(S.String),
    description: S.NullOr(S.String),
    url: S.NullOr(S.String),
    thumbnail: S.NullOr(S.UUID),
    /** Cover-image URL the Paperform sync writes; unset on hand-created courses, which carry only the `thumbnail` asset id. */
    thumbnail_paperform: S.NullOr(S.String),
    is_mandatory: S.Boolean,
    is_initial_audit: S.Boolean,
    language: S.NullOr(LanguageSchema),
    tags: S.NullOr(S.String),
    translation_group_id: S.NullOr(S.Int),
    contract_types: S.NullOr(S.Array(ContractType)),
    paperform_id: S.NullOr(S.String),
    paperform_slug: S.NullOr(S.String),
    paperform_custom_slug: S.NullOr(S.String),
    date_created: S.NullOr(S.String),
    metadata: S.Unknown,
});
export const ListOnlineCoursesParams = S.extend(PagingParams, S.Struct({
    language: S.optional(LanguageSchema),
    search: S.optional(S.String),
}));
export const ListOnlineCourses = HttpApiEndpoint.get("listOnlineCourses", "/admin/online-courses")
    .setUrlParams(ListOnlineCoursesParams)
    .addSuccess(pagedResponse(OnlineCourseRow))
    .addError(Forbidden, { status: 403 });
export const OnlineCourseNotFound = S.Struct({
    _tag: S.Literal("OnlineCourseNotFound"),
});
export const GetOnlineCourseParams = S.Struct({ id: S.UUID });
export const GetOnlineCourse = HttpApiEndpoint.get("getOnlineCourse", "/admin/online-courses/get")
    .setUrlParams(GetOnlineCourseParams)
    .addSuccess(S.Struct({ data: OnlineCourseRow }))
    .addError(OnlineCourseNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
export const CreateOnlineCoursePayload = S.Struct({
    title: S.NonEmptyString,
    url: S.NonEmptyString,
    description: S.optional(S.String),
    thumbnail: S.optional(S.UUID),
    is_mandatory: S.optional(S.Boolean),
    is_initial_audit: S.optional(S.Boolean),
    language: S.optional(LanguageSchema),
    tags: S.optional(S.String),
    translation_group_id: S.optional(S.Int),
    contract_types: S.optional(S.Array(ContractType)),
});
export const CreateOnlineCourse = HttpApiEndpoint.post("createOnlineCourse", "/admin/online-courses/create")
    .setPayload(CreateOnlineCoursePayload)
    .addSuccess(S.Struct({ data: OnlineCourseRow }))
    .addError(Forbidden, { status: 403 });
export const UpdateOnlineCoursePayload = S.Struct({
    id: S.UUID,
    title: S.optional(S.NonEmptyString),
    url: S.optional(S.NonEmptyString),
    description: S.optional(S.NullOr(S.String)),
    thumbnail: S.optional(S.NullOr(S.UUID)),
    is_mandatory: S.optional(S.Boolean),
    is_initial_audit: S.optional(S.Boolean),
    language: S.optional(S.NullOr(LanguageSchema)),
    tags: S.optional(S.NullOr(S.String)),
    contract_types: S.optional(S.NullOr(S.Array(ContractType))),
});
export const UpdateOnlineCourse = HttpApiEndpoint.post("updateOnlineCourse", "/admin/online-courses/update")
    .setPayload(UpdateOnlineCoursePayload)
    .addSuccess(S.Struct({ data: OnlineCourseRow }))
    .addError(OnlineCourseNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
export const DeleteOnlineCoursePayload = S.Struct({ id: S.UUID });
export const DeleteOnlineCourse = HttpApiEndpoint.post("deleteOnlineCourse", "/admin/online-courses/delete")
    .setPayload(DeleteOnlineCoursePayload)
    .addSuccess(S.Struct({ data: S.Struct({ deleted: S.Literal(true) }) }))
    .addError(OnlineCourseNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
/**
 * A course with `translation_group_id` set is one concept spanning
 * several per-language `online_courses` rows sharing that group id — the
 * admin catalog's delete action removes every language variant in one call
 * (mirroring `admin-risk-instances.deleteRiskInstancesByTranslationGroup`'s
 * shape), not just the row the caller happened to load. Courses carry no
 * `client_id`, so there is no per-client scoping to check.
 */
export const DeleteOnlineCoursesByTranslationGroupPayload = S.Struct({
    translation_group_id: S.Int,
});
export const DeleteOnlineCoursesByTranslationGroup = HttpApiEndpoint.post("deleteOnlineCoursesByTranslationGroup", "/admin/online-courses/delete-by-translation-group")
    .setPayload(DeleteOnlineCoursesByTranslationGroupPayload)
    .addSuccess(S.Struct({ data: S.Struct({ deleted: S.Int }) }))
    .addError(Forbidden, { status: 403 });
/**
 * A resolved `online_course_shares` recipient — the admin course editor's
 * "shared with" list. Only client-level rows (`user_id IS NULL`) qualify;
 * per-user shares are the sharing engine's concern
 * (`admin-sharing.verifyUserShares`), not this read model.
 */
export const OnlineCourseShareRow = S.Struct({
    client_id: S.UUID,
    client_name: S.NullOr(S.String),
});
export const ListOnlineCourseSharesParams = S.Struct({
    online_course_id: S.UUID,
});
export const ListOnlineCourseShares = HttpApiEndpoint.get("listOnlineCourseShares", "/admin/online-courses/shares")
    .setUrlParams(ListOnlineCourseSharesParams)
    .addSuccess(S.Struct({ items: S.Array(OnlineCourseShareRow) }))
    .addError(Forbidden, { status: 403 });
// ---------------------------------------------------------------------------
// paperform.* — Paperform is the online-course authoring tool; these two
// intents are the only way `online_courses` rows are populated/refreshed
// from it. Both wrap the legacy `PaperformService` verbatim (external HTTP
// call + the language/translation-group matching rules already implemented
// there) rather than re-deriving that logic.
// ---------------------------------------------------------------------------
export const SyncPaperformCoursesResult = S.Struct({
    message: S.String,
    /** Raw Paperform form objects, passed through undecoded — this intent's job is triggering the sync, not shaping Paperform's own response. */
    courses: S.Array(S.Unknown),
});
/**
 * Gated to `Principal.isAdmin` specifically (the legacy controller's own
 * `req.accountability.admin === false` check), not the broader `isOhsAdmin`
 * this group's other intents use — `PaperformClient`'s live layer runs the
 * wrapped `PaperformService` through a synthesized accountability keyed on
 * that same bit.
 */
export const SyncPaperformCourses = HttpApiEndpoint.post("syncPaperformCourses", "/admin/online-courses/paperform/sync")
    .addSuccess(S.Struct({ data: SyncPaperformCoursesResult }))
    .addError(Forbidden, { status: 403 });
export const RefreshPaperformCoursePayload = S.Struct({ id: S.UUID });
export const RefreshPaperformCourse = HttpApiEndpoint.post("refreshPaperformCourse", "/admin/online-courses/paperform/refresh")
    .setPayload(RefreshPaperformCoursePayload)
    .addSuccess(S.Struct({ data: S.Struct({ message: S.String }) }))
    .addError(OnlineCourseNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
export const adminOnlineCoursesGroup = HttpApiGroup.make("admin-online-courses")
    .add(ListOnlineCourses)
    .add(GetOnlineCourse)
    .add(CreateOnlineCourse)
    .add(UpdateOnlineCourse)
    .add(DeleteOnlineCourse)
    .add(DeleteOnlineCoursesByTranslationGroup)
    .add(ListOnlineCourseShares)
    .add(SyncPaperformCourses)
    .add(RefreshPaperformCourse)
    .middleware(PrincipalMiddleware);
//# sourceMappingURL=online-courses.js.map